import { useEffect, useState } from 'react';
import { BadgeCheck, Check, CircleSlash2, ExternalLink, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { InlineLoader } from '@/components/ui/InlineLoader';
import { SectionTitle } from '@/components/ui/SectionTitle';
import { adminPayoutsService } from '@/services/admin-payouts.service';
import { ApiError } from '@/services/http';
import type { AdminAffiliateRowView, AffiliateRequestView } from '@/types/api';

/**
 * AdminAffiliatesPage — `/admin/affiliates`.
 *
 * Single table view of every account that has produced at least one
 * affiliate accrual, sorted by lifetime earned descending. Each row
 * exposes a "Trigger payout now" button that runs the same
 * `payOutAffiliate` flow the monthly sweep uses (synchronous; the
 * idempotency key on the Stripe call means re-clicking can't double-
 * pay).
 *
 * v1 scope intentionally narrow — no per-accrual drilldown, no manual
 * adjustments. Both are easy follow-ups once the operational shape
 * settles.
 */
export function AdminAffiliatesPage() {
  const [rows, setRows] = useState<AdminAffiliateRowView[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminPayoutsService.list({ take: 100 });
      setRows(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load affiliates.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleTrigger = async (row: AdminAffiliateRowView) => {
    if (!row.hasStripeAccount) {
      toast.error('Affiliate has not connected a Stripe account yet.');
      return;
    }
    if (row.pendingBalanceCents <= 0) {
      toast.error('No pending balance to pay out.');
      return;
    }
    setBusyId(row.accountId);
    try {
      const { payoutId } = await adminPayoutsService.triggerPayout(row.accountId);
      if (payoutId) {
        toast.success(
          `Payout of ${formatMoney(row.pendingBalanceCents)} sent to ${row.firstName} ${row.lastName}.`,
        );
        await load();
      } else {
        toast.message('Nothing was paid out — check pending balance and Connect status.');
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to trigger payout.';
      toast.error(message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />

        <AffiliateRequestsSection />

        <div>
          <SectionTitle kicker="Admin" heading="Affiliates" />
          <p className="mt-1 text-[13px] text-or-slate-600">
            {total} {total === 1 ? 'affiliate has' : 'affiliates have'} earned commission
            so far.
          </p>
        </div>

        {loading ? (
          <InlineLoader label="Loading affiliates…" />
        ) : error ? (
          <div className="rounded-xl border border-or-rose-200 bg-or-rose-50 px-4 py-3 text-[13px] text-or-rose-700">
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-or-slate-200 bg-white px-6 py-12 text-center text-[14px] text-or-slate-500">
            No affiliates have earned commission yet. Once a referred account starts a paid
            subscription, the referrer will appear here.
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-or-slate-200 bg-white">
            <table className="w-full text-left text-[13px]">
              <thead className="bg-or-slate-50 text-[11px] uppercase tracking-wide text-or-slate-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Affiliate</th>
                  <th className="px-4 py-3 font-semibold">Connect</th>
                  <th className="px-4 py-3 text-right font-semibold">Paid referrals</th>
                  <th className="px-4 py-3 text-right font-semibold">Pending</th>
                  <th className="px-4 py-3 text-right font-semibold">Lifetime earned</th>
                  <th className="px-4 py-3 text-right font-semibold">Last paid</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-or-slate-100">
                {rows.map((row) => (
                  <tr key={row.accountId}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-or-slate-800">
                        {row.firstName} {row.lastName}
                      </div>
                      <div className="text-[12px] text-or-slate-500">
                        Code <code className="font-mono">{row.affiliateCode}</code>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {row.hasStripeAccount ? (
                        <span className="inline-flex items-center gap-1 text-or-emerald-700">
                          <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
                          Connected
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-or-slate-500">
                          <CircleSlash2 className="h-3.5 w-3.5" aria-hidden />
                          Not connected
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">{row.paidReferralsCount}</td>
                    <td className="px-4 py-3 text-right font-medium text-or-slate-800">
                      {formatMoney(row.pendingBalanceCents)}
                    </td>
                    <td className="px-4 py-3 text-right text-or-slate-600">
                      {formatMoney(row.lifetimeEarnedCents)}
                    </td>
                    <td className="px-4 py-3 text-right text-or-slate-500">
                      {row.lastPaidAt ? formatDate(row.lastPaidAt) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={
                          busyId === row.accountId ||
                          !row.hasStripeAccount ||
                          row.pendingBalanceCents <= 0
                        }
                        onClick={() => handleTrigger(row)}
                      >
                        <Send className="h-3.5 w-3.5" aria-hidden />
                        Pay now
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

/**
 * AffiliateRequestsSection — the pending "Request to Become an Affiliate"
 * review queue, shown above the earnings table. Each row carries the
 * applicant's submitted form (name, contact link, email) with Approve /
 * Reject controls. Approval is one click; rejection routes through a
 * ConfirmDialog because it is terminal — a rejected account can't re-apply
 * through the UI. Both decisions drop the row on success.
 */
function AffiliateRequestsSection() {
  const [requests, setRequests] = useState<AffiliateRequestView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<AffiliateRequestView | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setRequests(await adminPayoutsService.listRequests());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const decide = async (req: AffiliateRequestView, decision: 'approved' | 'rejected') => {
    setBusyId(req.accountId);
    try {
      await adminPayoutsService.decideRequest(req.accountId, decision);
      setRequests((prev) => prev.filter((r) => r.accountId !== req.accountId));
      toast.success(
        decision === 'approved'
          ? `Approved ${req.firstName} ${req.lastName} as an affiliate.`
          : `Rejected ${req.firstName} ${req.lastName}.`,
      );
      setRejectTarget(null);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to update request.';
      toast.error(message);
    } finally {
      setBusyId(null);
    }
  };

  // Hide the whole section when there's nothing pending — keeps the page
  // focused on the earnings table in the common case.
  if (!loading && !error && requests.length === 0) return null;

  return (
    <div>
      <SectionTitle kicker="Admin" heading="Pending affiliate requests" />
      <p className="mt-1 text-[13px] text-or-slate-600">
        Accounts waiting for approval to join the affiliate program.
      </p>

      <div className="mt-4">
        {loading ? (
          <InlineLoader label="Loading requests…" />
        ) : error ? (
          <div className="rounded-xl border border-or-rose-200 bg-or-rose-50 px-4 py-3 text-[13px] text-or-rose-700">
            {error}
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-or-slate-200 bg-white">
            <table className="w-full text-left text-[13px]">
              <thead className="bg-or-slate-50 text-[11px] uppercase tracking-wide text-or-slate-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Applicant</th>
                  <th className="px-4 py-3 font-semibold">Website / social</th>
                  <th className="px-4 py-3 font-semibold">Submitted</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-or-slate-100">
                {requests.map((req) => (
                  <tr key={req.accountId}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-or-slate-800">
                        {req.firstName} {req.lastName}
                      </div>
                      <div className="text-[12px] text-or-slate-500">{req.contactEmail}</div>
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={req.contactUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex max-w-[260px] items-center gap-1 truncate text-or-blue-600 hover:underline"
                      >
                        <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        <span className="truncate">{req.contactUrl}</span>
                      </a>
                    </td>
                    <td className="px-4 py-3 text-or-slate-500">{formatDate(req.submittedAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="success"
                          disabled={busyId === req.accountId}
                          onClick={() => decide(req, 'approved')}
                        >
                          <Check className="h-3.5 w-3.5" aria-hidden />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={busyId === req.accountId}
                          onClick={() => setRejectTarget(req)}
                        >
                          <X className="h-3.5 w-3.5" aria-hidden />
                          Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={rejectTarget !== null}
        onOpenChange={(next) => !next && setRejectTarget(null)}
        title="Reject affiliate request?"
        description={
          rejectTarget
            ? `${rejectTarget.firstName} ${rejectTarget.lastName} won't be able to re-apply through the app. Only a manual reset can undo this.`
            : undefined
        }
        confirmLabel="Reject request"
        confirmVariant="danger"
        isConfirming={rejectTarget !== null && busyId === rejectTarget.accountId}
        onConfirm={() => {
          if (rejectTarget) void decide(rejectTarget, 'rejected');
        }}
      />
    </div>
  );
}

function formatMoney(cents: number): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
      cents / 100,
    );
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
